export interface McpPromptDescriptor {
  name: string
  description?: string
  arguments?: Array<{
    name: string
    description?: string
    required?: boolean
  }>
}

export interface McpPromptGetResult {
  description?: string
  messages: Array<{
    role: 'user' | 'assistant' | 'system'
    content: Array<{
      type: 'text'
      text: string
    }>
  }>
}

const MCP_PROMPT_DESCRIPTORS: McpPromptDescriptor[] = [
  {
    name: 'plan_short_video',
    description: 'Generate a short-video edit plan for the current project materials.',
    arguments: [
      { name: 'topic', description: 'Short description of the target short-video topic.', required: true },
      { name: 'durationSeconds', description: 'Target duration in seconds.', required: false },
      { name: 'tone', description: 'Tone such as natural/professional/friendly/energetic/calm.', required: false },
    ],
  },
  {
    name: 'continue_from_plan',
    description: 'Continue execution from the current draft or running plan using execute_plan_step / get_plan_status.',
    arguments: [
      { name: 'goal', description: 'What should be completed next.', required: false },
    ],
  },
  {
    name: 'validate_before_export',
    description: 'Review project state before export and identify blocking issues.',
    arguments: [
      { name: 'exportGoal', description: 'Target platform or export intent.', required: false },
    ],
  },
]

export function getMcpPromptCatalog(): McpPromptDescriptor[] {
  return [...MCP_PROMPT_DESCRIPTORS]
}

export async function getMcpPrompt(
  name: string,
  arguments_: Record<string, unknown> | undefined,
): Promise<McpPromptGetResult> {
  switch (name) {
    case 'plan_short_video': {
      const topic = typeof arguments_?.topic === 'string' ? arguments_.topic : '当前项目素材'
      const duration = typeof arguments_?.durationSeconds === 'number' ? arguments_.durationSeconds : 30
      const tone = typeof arguments_?.tone === 'string' ? arguments_.tone : 'natural'
      return {
        description: 'Draft a short-video plan for the current project.',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text:
                  `请基于当前打开项目和现有素材，为我生成一条短视频编辑计划。` +
                  `\n主题：${topic}` +
                  `\n目标时长：${duration} 秒` +
                  `\n语气：${tone}` +
                  `\n请优先调用 analyze_current_project_structure、analyze_project_media 和 draft_edit_plan。`,
              },
            ],
          },
        ],
      }
    }
    case 'continue_from_plan': {
      const goal = typeof arguments_?.goal === 'string' ? arguments_.goal : '继续执行当前计划'
      return {
        description: 'Continue from the current draft or running plan.',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text:
                  `${goal}。` +
                  `\n请先调用 get_plan_status 读取当前草稿计划 / 运行中 job。` +
                  `\n若有 draft plan，则先调用 confirm_plan。` +
                  `\n随后用 execute_plan_step 按 stepId / stepIndex 单步推进，并在每步后再次调用 get_plan_status。`,
              },
            ],
          },
        ],
      }
    }
    case 'validate_before_export': {
      const exportGoal = typeof arguments_?.exportGoal === 'string' ? arguments_.exportGoal : '常规导出'
      return {
        description: 'Validate current project before export.',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text:
                  `请在导出前检查当前项目是否存在明显问题。` +
                  `\n导出目标：${exportGoal}` +
                  `\n请优先调用 read_current_project_disk_snapshot、analyze_current_project_structure、summarize_timeline、get_runtime_capabilities，必要时再决定是否调用 start_export。`,
              },
            ],
          },
        ],
      }
    }
    default:
      throw new Error(`Unknown MCP prompt: ${name}`)
  }
}
